const URL_REGEX = /(https?:\/\/[^\s<>"']+)/g;

const ALLOWED_TAGS = new Set([
  "p",
  "br",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "s",
  "strike",
  "del",
  "ul",
  "ol",
  "li",
  "a",
  "h1",
  "h2",
  "h3",
  "h4",
  "pre",
  "blockquote",
  "span",
  "div",
  "sub",
  "sup",
  "font",
  "img",
]);

const FORBIDDEN_TAGS = new Set([
  "script",
  "iframe",
  "object",
  "embed",
  "form",
  "input",
  "textarea",
  "button",
  "select",
  "meta",
  "link",
  "style",
  "base",
  "svg",
  "math",
]);

const GLOBAL_ALLOWED_ATTRS = new Set(["class", "title"]);

const TAG_ALLOWED_ATTRS: Record<string, Set<string>> = {
  a: new Set(["href", "target", "rel", "data-rich-text-href", "role", "tabindex", "id", "name"]),
  span: new Set(["style"]),
  p: new Set(["style"]),
  div: new Set(["style"]),
  li: new Set(["style"]),
  font: new Set(["color", "face", "size"]),
  img: new Set(["src", "alt", "class", "title", "width", "height"]),
};

const ALLOWED_STYLE_PROPS = new Set([
  "color",
  "background-color",
  "text-align",
  "font-weight",
  "font-style",
  "font-size",
  "text-decoration",
]);

export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

export function containsHtmlMarkup(value: string): boolean {
  return /<\/?[a-z][\s\S]*?>/i.test(value);
}

export function linkifyPlainText(value: string | null | undefined): string {
  if (value == null || value === "") {
    return "";
  }

  const parts = value.split(URL_REGEX);
  return parts
    .map(part => {
      if (/^https?:\/\//.test(part)) {
        const url = part.trim();
        const href = escapeAttr(url);
        const text = escapeHtml(url);
        return `<a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>`;
      }
      return escapeHtml(part).replace(/\n/g, "<br>");
    })
    .join("");
}

function isSafeUrl(url: string): boolean {
  const trimmed = url.trim().replace(/\s/g, "").toLowerCase();
  if (!trimmed) {
    return false;
  }

  if (
    trimmed.startsWith("javascript:") ||
    trimmed.startsWith("data:") ||
    trimmed.startsWith("vbscript:") ||
    trimmed.startsWith("file:")
  ) {
    return false;
  }

  return (
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("mailto:") ||
    trimmed.startsWith("tel:") ||
    trimmed.startsWith("/") ||
    trimmed.startsWith("#") ||
    trimmed.startsWith("./") ||
    trimmed.startsWith("../")
  );
}

function sanitizeStyle(style: string): string {
  return style
    .split(";")
    .map(part => part.trim())
    .filter(Boolean)
    .map(part => {
      const colonIndex = part.indexOf(":");
      if (colonIndex === -1) {
        return "";
      }

      const name = part.slice(0, colonIndex).trim().toLowerCase();
      const value = part.slice(colonIndex + 1).trim();
      if (!ALLOWED_STYLE_PROPS.has(name) || !value) {
        return "";
      }

      if (/expression|javascript|vbscript|behavior|-moz-binding|url\s*\(\s*['"]?\s*javascript/i.test(value)) {
        return "";
      }

      return `${name}: ${value}`;
    })
    .filter(Boolean)
    .join("; ");
}

function isSafeAnchorName(value: string): boolean {
  return /^[a-zA-Z][\w\-:.]*$/.test(value.trim());
}

function isSafeAnchorImgSrc(src: string): boolean {
  const trimmed = src.trim().toLowerCase();
  return trimmed.startsWith("data:image/gif;base64,") || isSafeUrl(trimmed);
}

function sanitizeAttributes(element: HTMLElement, tag: string): void {
  const allowedAttrs = new Set<string>([
    ...Array.from(GLOBAL_ALLOWED_ATTRS),
    ...Array.from(TAG_ALLOWED_ATTRS[tag] || []),
  ]);

  Array.from(element.attributes).forEach(attr => {
    const name = attr.name.toLowerCase();
    if (name.startsWith("on") || !allowedAttrs.has(name)) {
      element.removeAttribute(attr.name);
      return;
    }

    if (name === "href" && !isSafeUrl(attr.value)) {
      element.removeAttribute(attr.name);
      return;
    }

    if (name === "src" && tag === "img" && !isSafeAnchorImgSrc(attr.value)) {
      element.removeAttribute(attr.name);
      return;
    }

    if ((name === "id" || name === "name") && !isSafeAnchorName(attr.value)) {
      element.removeAttribute(attr.name);
      return;
    }

    if (name === "style") {
      const cleanStyle = sanitizeStyle(attr.value);
      if (cleanStyle) {
        element.setAttribute("style", cleanStyle);
      } else {
        element.removeAttribute("style");
      }
    }
  });

  if (tag === "a" && element.hasAttribute("href")) {
    const rawHref = element.getAttribute("href") || "";
    const href = rawHref.trim().replace(/\s/g, "").toLowerCase();
    const isMailOrTel = href.startsWith("mailto:") || href.startsWith("tel:");

    if (isMailOrTel) {
      element.setAttribute("data-rich-text-href", rawHref.trim());
      element.removeAttribute("href");
      element.removeAttribute("target");
      element.removeAttribute("rel");
      element.setAttribute("role", "link");
      element.setAttribute("tabindex", "0");
      element.classList.add("rich-text-action-link");
      return;
    }

    const opensInNewTab = href.startsWith("http://") || href.startsWith("https://");

    if (opensInNewTab) {
      element.setAttribute("target", "_blank");
      element.setAttribute("rel", "noopener noreferrer");
    } else {
      element.removeAttribute("target");
      element.removeAttribute("rel");
    }
  }
}

function unwrapElement(element: Element): void {
  const parent = element.parentNode;
  if (!parent) {
    element.remove();
    return;
  }

  while (element.firstChild) {
    parent.insertBefore(element.firstChild, element);
  }
  element.remove();
}

function normalizeCkeditorAnchorPlaceholders(container: HTMLElement): void {
  Array.from(container.querySelectorAll('img[data-cke-real-element-type="anchor"]')).forEach(img => {
    const encodedAnchor = img.getAttribute("data-cke-realelement");
    if (!encodedAnchor) {
      return;
    }

    try {
      const decoded = decodeURIComponent(encodedAnchor);
      const temp = document.createElement("div");
      temp.innerHTML = decoded;
      const anchor = temp.querySelector("a");
      if (anchor && img.parentNode) {
        img.parentNode.replaceChild(anchor, img);
      }
    } catch {
      // Keep the image placeholder if decoding fails.
    }
  });
}

function sanitizeNode(node: Node): void {
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return;
  }

  const element = node as Element;
  Array.from(element.childNodes).forEach(child => sanitizeNode(child));

  const tag = element.tagName.toLowerCase();
  if (FORBIDDEN_TAGS.has(tag)) {
    element.remove();
    return;
  }

  if (tag === "img") {
    const className = (element.getAttribute("class") || "").toLowerCase();
    if (!className.includes("cke_anchor")) {
      element.remove();
      return;
    }
    sanitizeAttributes(element as HTMLElement, tag);
    return;
  }

  if (!ALLOWED_TAGS.has(tag)) {
    unwrapElement(element);
    return;
  }

  sanitizeAttributes(element as HTMLElement, tag);
}

export function sanitizeRichTextHtml(html: string): string {
  if (!html) {
    return "";
  }

  const container = document.createElement("div");
  container.innerHTML = html;
  normalizeCkeditorAnchorPlaceholders(container);
  sanitizeNode(container);
  return container.innerHTML.replace(/>\s+</g, "><");
}

export function prepareRichTextContent(value: string | null | undefined): string {
  if (value == null || value === "") {
    return "";
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return "";
  }

  if (containsHtmlMarkup(trimmed)) {
    return sanitizeRichTextHtml(trimmed);
  }

  return linkifyPlainText(trimmed);
}

export function stripHtmlTags(html: string): string {
  if (!html) {
    return "";
  }

  const container = document.createElement("div");
  container.innerHTML = html;
  return (container.textContent || container.innerText || "").trim();
}

export function getPlainTextLength(html: string): number {
  return stripHtmlTags(html).length;
}

export function isRichTextEmpty(html: string): boolean {
  return getPlainTextLength(html) === 0;
}

export function isRichTextTooLong(html: string, maxLength: number): boolean {
  return getPlainTextLength(html) > maxLength;
}

function findRichTextSpecialLink(target: Element): Element | null {
  const dataLink = target.closest("[data-rich-text-href]");
  if (dataLink) {
    return dataLink;
  }

  const anchor = target.closest("a");
  if (!anchor) {
    return null;
  }

  const href = (anchor.getAttribute("href") || "").trim().replace(/\s/g, "").toLowerCase();
  if (href.startsWith("mailto:") || href.startsWith("tel:") || href.startsWith("#")) {
    return anchor;
  }

  return null;
}

function getRichTextSpecialLinkHref(link: Element): string | null {
  const dataHref = link.getAttribute("data-rich-text-href");
  if (dataHref) {
    return dataHref.trim();
  }

  const href = link.getAttribute("href");
  return href ? href.trim() : null;
}

function findScrollableParent(element: Element): HTMLElement | null {
  let current: Element | null = element.parentElement;

  while (current && current !== document.body) {
    if (!(current instanceof HTMLElement)) {
      current = current.parentElement;
      continue;
    }

    const style = window.getComputedStyle(current);
    const canScroll =
      (style.overflowY === "auto" || style.overflowY === "scroll" || style.overflowY === "overlay") &&
      current.scrollHeight > current.clientHeight;

    if (canScroll) {
      return current;
    }

    current = current.parentElement;
  }

  return null;
}

function escapeCssSelector(value: string): string {
  const cssEscape = (CSS as { escape?: (id: string) => string }).escape;
  return typeof cssEscape === "function" ? cssEscape(value) : value.replace(/"/g, '\\"');
}

function findRichTextAnchorTarget(container: Element, anchorId: string): Element | null {
  const escapedId = escapeCssSelector(anchorId);

  return (
    container.querySelector(`#${escapedId}`) ||
    container.querySelector(`a[name="${escapedId}"]`) ||
    document.getElementById(anchorId) ||
    document.querySelector(`a[name="${escapedId}"]`)
  );
}

function getStickyScrollOffset(scrollParent: Element | null): number {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return 0;
  }

  let offset = 0;
  Array.from(document.querySelectorAll(".main-header, .sticky-top, header.sticky-top")).forEach(element => {
    if (!(element instanceof HTMLElement)) {
      return;
    }

    const style = window.getComputedStyle(element);
    if (style.position !== "sticky" && style.position !== "fixed") {
      return;
    }

    if (scrollParent && !scrollParent.contains(element)) {
      return;
    }

    const height = element.getBoundingClientRect().height;
    if (height > 0) {
      offset = Math.max(offset, height);
    }
  });

  return offset;
}

function scrollToRichTextAnchor(container: Element, anchorId: string): void {
  let decodedId = anchorId;
  try {
    decodedId = decodeURIComponent(anchorId);
  } catch {
    decodedId = anchorId;
  }

  const target = findRichTextAnchorTarget(container, decodedId);

  if (!target || typeof window === "undefined") {
    return;
  }

  const scrollParent = findScrollableParent(target);
  const targetRect = target.getBoundingClientRect();
  const stickyOffset = getStickyScrollOffset(scrollParent);

  if (scrollParent) {
    const parentRect = scrollParent.getBoundingClientRect();
    const top = scrollParent.scrollTop + (targetRect.top - parentRect.top) - stickyOffset;
    scrollParent.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    return;
  }

  const top = targetRect.top + window.pageYOffset - stickyOffset;
  window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
}

export function openRichTextSpecialLink(href: string): void {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.setAttribute("tabindex", "-1");
  iframe.style.cssText =
    "position:absolute;width:0;height:0;border:0;opacity:0;pointer-events:none;visibility:hidden";
  iframe.src = href;
  document.body.appendChild(iframe);
  window.setTimeout(() => {
    iframe.remove();
  }, 1000);
}

export function handleRichTextSpecialLinkClick(event: MouseEvent | KeyboardEvent): boolean {
  if (event instanceof KeyboardEvent) {
    if (event.key !== "Enter" && event.key !== " ") {
      return false;
    }
  }

  const target = event.target;
  if (!(target instanceof Element)) {
    return false;
  }

  const link = findRichTextSpecialLink(target);
  if (!link || !link.closest(".rich-text-content")) {
    return false;
  }

  const href = getRichTextSpecialLinkHref(link);
  if (!href) {
    return false;
  }

  event.preventDefault();
  event.stopPropagation();

  if (href.startsWith("#")) {
    const container = link.closest(".rich-text-content");
    if (container) {
      scrollToRichTextAnchor(container, href.slice(1));
    }
    return true;
  }

  openRichTextSpecialLink(href);
  return true;
}
